import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Spinner, Alert, Form, ListGroup, Badge } from 'react-bootstrap';
import dayjs from 'dayjs';
import {
  InstrumentMaintenanceRecord,
  getInstrumentMaintenanceRecords,
  addInstrumentMaintenanceRecord
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ResponsiblePersonMultiSelect, {
  ResponsibleOptionUser
} from './ResponsiblePersonMultiSelect';

interface InstrumentMaintenanceRecordsProps {
  instrumentId: string;
  instrumentName: string;
  onClose: () => void;
}

function recordMaintainersLabel(record: InstrumentMaintenanceRecord): string {
  if (record.maintainers && record.maintainers.trim()) {
    return record.maintainers.trim();
  }
  const u = record.createdBy;
  if (!u || typeof u === 'string') return '—';
  return u.name || u.username || '—';
}

function recordSubmitterLabel(record: InstrumentMaintenanceRecord): string | null {
  const u = record.createdBy;
  if (!u || typeof u === 'string') return null;
  const name = u.name || u.username;
  if (!name) return null;
  const maintainers = (record.maintainers || '').trim();
  if (!maintainers) return null;
  if (maintainers.split(/[、,，;\s]+/).some((n) => n.trim() === name)) return null;
  return name;
}

const InstrumentMaintenanceRecords: React.FC<InstrumentMaintenanceRecordsProps> = ({
  instrumentId,
  instrumentName,
  onClose
}) => {
  const { user, token } = useAuth();
  const [records, setRecords] = useState<InstrumentMaintenanceRecord[]>([]);
  const [users, setUsers] = useState<ResponsibleOptionUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [content, setContent] = useState('');
  const [performedAt, setPerformedAt] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [selectedMaintainers, setSelectedMaintainers] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');

  const fetchUsers = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch('/api/auth/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userData = await response.json();
      if (Array.isArray(userData)) {
        const list = userData.filter((u: ResponsibleOptionUser) => u.role !== '超级管理员');
        setUsers(list);
        if (user?.name && list.some((u) => u.name === user.name)) {
          setSelectedMaintainers((prev) =>
            prev.length > 0 ? prev : [user.name]
          );
        }
      }
    } catch (e) {
      console.error('获取成员列表失败:', e);
    }
  }, [token, user?.name]);

  const fetchRecords = useCallback(async () => {
    if (!token) {
      setError('请先登录后查看维护记录');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const response = await getInstrumentMaintenanceRecords(instrumentId);
      if (response.success) {
        setRecords(response.data || []);
      } else {
        setError(response.error || '获取维护记录失败');
      }
    } catch {
      setError('获取维护记录失败');
    } finally {
      setLoading(false);
    }
  }, [instrumentId, token]);

  useEffect(() => {
    void fetchRecords();
    void fetchUsers();
  }, [fetchRecords, fetchUsers]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setSubmitError('请先登录');
      return;
    }
    const trimmed = content.trim();
    if (!trimmed) {
      setSubmitError('请填写维护内容');
      return;
    }
    if (selectedMaintainers.length === 0) {
      setSubmitError('请至少选择一名维护人');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess('');
    try {
      const response = await addInstrumentMaintenanceRecord(instrumentId, {
        content: trimmed,
        maintainers: selectedMaintainers.join('、'),
        performedAt: performedAt ? `${performedAt}T12:00:00` : undefined
      });
      if (response.success && response.data) {
        setRecords((prev) => [response.data!, ...prev]);
        setContent('');
        setPerformedAt(dayjs().format('YYYY-MM-DD'));
        setSelectedMaintainers(user?.name ? [user.name] : []);
        setSubmitSuccess('维护记录已添加');
      } else {
        setSubmitError(response.error || '添加失败');
      }
    } catch {
      setSubmitError('网络错误，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show onHide={onClose} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>维护记录 — {instrumentName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <div className="mt-3 text-muted">加载中…</div>
          </div>
        ) : (
          <>
            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}

            {token && user && (
              <div className="mb-4 p-3 rounded" style={{ background: 'rgba(243, 244, 245, 0.6)' }}>
                <h6 className="mb-3">添加维护记录</h6>
                <Form onSubmit={(e) => void handleSubmit(e)}>
                  <Form.Group className="mb-3">
                    <Form.Label>维护人 *</Form.Label>
                    <ResponsiblePersonMultiSelect
                      users={users}
                      selectedNames={selectedMaintainers}
                      onChange={setSelectedMaintainers}
                      searchPlaceholder="搜索成员姓名/账号/角色"
                      helperText="可多选，记录实际执行维护的人员（不限于登记人）。"
                      removeMemberLabel="维护人"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>维护日期</Form.Label>
                    <Form.Control
                      type="date"
                      value={performedAt}
                      onChange={(e) => setPerformedAt(e.target.value)}
                      max={dayjs().format('YYYY-MM-DD')}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>维护内容 *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="如：定期保养、校准、更换配件、故障排除等"
                      maxLength={5000}
                      required
                    />
                  </Form.Group>
                  {submitError && <Alert variant="danger">{submitError}</Alert>}
                  {submitSuccess && <Alert variant="success">{submitSuccess}</Alert>}
                  <Button type="submit" variant="primary" disabled={submitting}>
                    {submitting ? '提交中…' : '提交记录'}
                  </Button>
                </Form>
                <Form.Text className="text-muted d-block mt-2">
                  团队成员均可登记维护记录。若该仪器设置了维护周期，提交后将自动重置周期起算日。
                </Form.Text>
              </div>
            )}

            {!token && !error && (
              <Alert variant="warning">登录后可查看并添加维护记录。</Alert>
            )}

            <h6 className="mb-3">
              历史记录
              <Badge bg="secondary" className="ms-2">
                {records.length}
              </Badge>
            </h6>

            {records.length === 0 ? (
              <p className="text-muted mb-0">暂无维护记录，成为第一个记录维护情况的成员吧。</p>
            ) : (
              <ListGroup variant="flush">
                {records.map((record) => {
                  const submitter = recordSubmitterLabel(record);
                  return (
                    <ListGroup.Item key={record._id} className="px-0 py-3 border-bottom">
                      <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                        <div>
                          <span className="text-muted small me-1">维护人</span>
                          <strong>{recordMaintainersLabel(record)}</strong>
                          {submitter && (
                            <span className="text-muted small ms-2">登记：{submitter}</span>
                          )}
                        </div>
                        <span className="text-muted small">
                          维护日期：{dayjs(record.performedAt || record.createdAt).format('YYYY-MM-DD')}
                          {record.createdAt && (
                            <span className="ms-2">
                              登记于 {dayjs(record.createdAt).format('YYYY-MM-DD HH:mm')}
                            </span>
                          )}
                        </span>
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{record.content}</div>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          关闭
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InstrumentMaintenanceRecords;
